import { CANCELLATION_STATUS, SERVICE_TYPE, STATUS } from '~/app/utils/OrderListUtils'
import { DateTime } from 'luxon'
import { ConvertedOrder } from '~/app/components/order-converter/getCartOrderConverter'
import { isTypeOf, isTruthy } from '~/app/utils'
import { OrderDetailsCancellationCategoryOrders } from '~/app/components/order-list/hooks/useCancelInfo'
import { OrderListV3Query } from '~/__generated__/app/query/order-list/order-list.graphql'
import { OrderDetailsV3Query } from '~/__generated__/app/query/order-details/order-details.graphql'
import { SharedOrderQuery } from '~/__generated__/app/query/order-shared/order-shared-list.graphql'
import { OrderCompleteV3Query } from '~/__generated__/app/query/order-complete/order-complete.graphql'
import {
  CancelPolicySummary,
  CategoryType,
  DomesticAccommodationUseType,
  PendingStatus,
  PickupType,
  ReviewWritableResponseV2,
  Role,
  StatusGroupCode,
  VisitType,
} from '~/__generated__/__types__'
import conf from '~/app/conf'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import {
  BrazeVisitType,
  Category,
  Currency,
  IBrazePurchaseEventLog,
} from '~/app/braze/brazeCustomLog'
import {
  badgeWithPolicyInOrder,
  getHasDayUseBadge,
  isDayUse,
} from '~/api/utils/accommodationBadgesWithPolicy'
import {
  getBrazeOrderCategory,
  getOrderCompleteBrazeCouponAndPointInfo,
  IConverterBrazeCompositeOrderComplete,
} from '~/app/components/order-converter/useOrderCompleteBrazeCustomConverter'
import { toCoin, toPoint } from '~/app/utils/krNumberToString'
import { IFbqPurchaseData } from '~/app/pixel'
import { getDomesticRateplanBenefits } from '~/app/utils/domesticRateplan'
import getReviewWritableButton from '~/app/utils/getReviewWritableButton'
import { CartOrderListPageLog } from '~/app/kinesis/protobuf/clientlogs/order-misc-common-txtpb'
import { ConfigBadgeType, TConfigBadgeType } from '~/api/utils/leisure'
import { getDomesticVipBadgeType } from '~/api/utils/memberClass'

import { getUseTimeText } from '~/api/converters/domestic/order/ProductSection/Property/utils'
import {
  OrderButton,
  OrderButtonIconType,
  OrderButtonType,
} from '~/app/components/order-converter/types'

const UseTypeText = {
  [DomesticAccommodationUseType.DayUse]: '대실',
  [DomesticAccommodationUseType.OverNight]: '숙박',
  [DomesticAccommodationUseType.MultiNights]: '숙박',
}

export const getDomesticStatus = (
  status: StatusGroupCode,
  checkOut: string,
  currentAt?: string
) => {
  if (StatusGroupCode.Cancel === status) {
    return STATUS.CANCEL_COMPLETED
  }
  const now = currentAt ? DateTime.fromISO(currentAt) : DateTime.now()
  const isCheckedOut = DateTime.fromISO(checkOut).diff(now).toMillis() < 0
  if (isCheckedOut) {
    return STATUS.COMPLETED
  }
  return STATUS.READY
}

/**
 *  @desc api 에서는 체크인 이후부터는 nonCancellationReason 을 'COMPLETED'로 내려주고 있다.
 *        체크인 이후 체크아웃 이전에는 취소 규정 노출이 필요하기 때문에 해당 조건의 경우 nonCancellationReason 을 'NON_REFUNDABLE'으로 변경한다.
 *  @jira https://yanolja.atlassian.net/browse/DEFECT-22419
 * */
export const getDomesticCancelPolicySummary = ({
  cancelPolicySummary,
  statusGroupCode,
  currentAt,
  checkIn,
  checkOut,
}: {
  cancelPolicySummary?: CancelPolicySummary
  statusGroupCode: StatusGroupCode
  currentAt: string
  checkIn: string
  checkOut: string
}) => {
  if (!cancelPolicySummary) return undefined
  // 취소 완료인 경우 cancelPolicySummary 를 그대로 반환하여 취소 규정이 미노출되도록 한다.
  if (statusGroupCode === StatusGroupCode.Cancel) return cancelPolicySummary

  const now = currentAt ? DateTime.fromISO(currentAt) : DateTime.now()
  const isCheckedIn = DateTime.fromISO(checkIn) < now
  const isCheckedOut = DateTime.fromISO(checkOut) < now

  if (isCheckedIn && !isCheckedOut) {
    return {
      ...cancelPolicySummary,
      nonCancellationReason: 'NON_REFUNDABLE',
    }
  }

  return cancelPolicySummary
}

export const getCancellationDomesticStatus = (status: StatusGroupCode) =>
  StatusGroupCode.Cancel === status
    ? CANCELLATION_STATUS.COMPLETED
    : CANCELLATION_STATUS.IN_PROGRESS

export type TConvertedProductInfo = (string | { text: string; style: string })[][]
// 숙소 정보 (대실여부, 체크인/아웃 날짜, 기준인원)
export const getDomesticInfo = ({
  checkIn,
  checkOut,
  useType,
  roomType,
}: {
  checkIn: string
  checkOut: string
  useType: DomesticAccommodationUseType
  roomType?: Props['roomType']
}): TConvertedProductInfo => {
  const roomTypeBadges = roomType?.config?.badges ?? []
  const isDayUse = getHasDayUseBadge(roomTypeBadges)
  const isRent = useType === DomesticAccommodationUseType.DayUse
  const durationText =
    isRent || isDayUse
      ? isoToFormat(checkIn, 'yyyy.MM.dd (ccc)')
      : [checkIn, checkOut].map(date => isoToFormat(date, 'yyyy.MM.dd (ccc)')).join(' ~ ')
  const nights = getUseTimeText(checkIn, checkOut, isRent)
  const { capacity, maxCapacity } = roomType?.data || {}

  // 국내숙소의 경우 날짜, 연박수는 검정색으로 표시한다.
  return [
    isDayUse
      ? [
          {
            text: durationText,
            style: 'black',
          },
        ]
      : [
          {
            text: durationText,
            style: 'black',
          },
          {
            text: nights,
            style: 'black',
          },
        ],
    [`체크인 ${isoToFormat(checkIn, 'HH:mm')}`, `체크아웃 ${isoToFormat(checkOut, 'HH:mm')}`],
    capacity && maxCapacity ? [`기준 ${capacity}명 / 최대 ${maxCapacity}명`] : undefined,
  ].filter(isTruthy)
}

/**
 * '요금제 옵션' 항목.
 * Post Order에서는 rate plan 요금제의 조식 제공 여부, 체크인/체크아웃과 방문 형태, 픽업 형태를 조합해 반환한다.
 * 노출 순서 : 조식 > 체크인 > 체크아웃 > 주차 가능여부(주차불가) > 픽업 요청
 */
export const getDomesticOptions = ({ benefits, visitType, pickupType }) => {
  const { breakfast, checkIn, checkOut } = getDomesticRateplanBenefits(benefits) || {}

  const visitTypeText =
    visitType === VisitType.Walk
      ? '도보방문'
      : visitType === VisitType.PublicTransport
        ? '대중교통'
        : visitType === VisitType.OwnVehicle || visitType === VisitType.Vehicle
          ? '차량방문'
          : visitType === VisitType.ParkingUnavailable
            ? '주차불가'
            : ''

  const options = [
    breakfast,
    checkIn,
    checkOut,
    visitTypeText,
    pickupType === PickupType.Pickup && '픽업요청',
  ].filter(isTruthy)

  return options.join(' ・ ') || undefined
}

export const getIsDomesticPendingReservation = (
  pendingStatus: PendingStatus | undefined,
  status: STATUS
): boolean => {
  const availableStatuses = [STATUS.READY, STATUS.COMPLETED]
  return pendingStatus === PendingStatus.Pending && availableStatuses.includes(status)
}

export const getDomesticPendingReservationBadges = (
  isPendingStatus: boolean
): TConfigBadgeType[] => {
  return isPendingStatus ? [ConfigBadgeType.PENDING_RESERVATION] : []
}

export const getDomesticCancelPenaltyBadges = (isPenaltyAgree: boolean): TConfigBadgeType[] => {
  return isPenaltyAgree ? [ConfigBadgeType.CANCEL_PENALTY] : []
}

export const getDomesticButtons = ({
  props,
  currentAt,
  isPendingReservation,
  order,
}: {
  props: Props
  currentAt: string
  isPendingReservation: boolean
  order: { id: string; hashOrderId: string }
}) => {
  const {
    id,
    hashOrderId,
    statusGroupCode,
    propertyId,
    checkInAt: checkIn,
    checkOutAt: checkOut,
    roomTypeId,
    ratePlanId,
    ratePlanVersion,
    propertyVersion = '',
    roomTypeVersion = '',
    cancelPolicyId = '',
    nolChat,
  } = props

  const buttons: Array<OrderButton> = []
  const orderStatus = getDomesticStatus(statusGroupCode, checkOut, currentAt)

  /**
   * 상품 이용 전 상태일 경우에만 예약 취소 버튼 추가
   * 단, 체크아웃 이후에도 대기예약 상품은 취소 가능
   */
  if (orderStatus === STATUS.READY || isPendingReservation) {
    buttons.push({
      type: OrderButtonType.BASIC,
      title: '예약 취소',
      id: 'cancel',
      isDisabled: false,
      data: {
        id,
        categoryOrderId: id,
      },
    })
  }

  // 이용정보
  if (orderStatus === STATUS.READY) {
    buttons.push({
      type: OrderButtonType.BASIC,
      title: '이용 정보',
      id: 'information',
      data: {
        type: 'domestic',
        propertyId,
        roomTypeId,
        checkIn,
        checkOut,
        propertyVersion,
        roomTypeVersion,
        cancelPolicyId,
        categoryOrderId: id,
        ratePlanId,
        ratePlanVersion,
      },
    })
  }

  const kinesisLogMeta: CartOrderListPageLog.OrderProdInfoButtonWriteReviewBtn.Impression = {
    hashOrderId: order.hashOrderId,
    hashCategoryOrderId: hashOrderId,
    productType: 'accommodation_korea',
    placeNo: String(propertyId),
    roomTypeNo: String(roomTypeId),
    ratePlanNo: String(ratePlanId),
  }

  /**
   * 후기
   * 예약내역, 예약상세 페이지에서 후기 작성 버튼 노출 가능.
   * 단, 대기예약 상태인 경우 후기 작성 노출하지 않음.
   */
  if (
    !isPendingReservation &&
    (isOrderDetailsDomesticOrder(props) || isOrderListsDomesticOrder(props))
  ) {
    if (props.reviewWritableV2) {
      const reviewButton = getReviewWritableButton(props.reviewWritableV2, currentAt)

      if (reviewButton) {
        buttons.push({
          ...reviewButton,
          data: {
            ...reviewButton.data,
            target: 'domestic',
            kinesisLogMeta: { ...kinesisLogMeta, activeYn: !reviewButton.isDisabled },
          },
        })
      }
    }
  }

  // 셀프 체크인 QR : 대기 예약은 QR체크인 버튼 미노출
  if (orderStatus === STATUS.READY && !isPendingReservation) {
    const hasSelfCheckIn = props.productConfig?.control.selfCheckIn
    if (hasSelfCheckIn) {
      buttons.push({
        type: OrderButtonType.PRIMARY,
        title: '셀프 체크인 QR',
        id: 'self-checkin',
        icon: OrderButtonIconType.QR,
        data: { ...props, kinesisLogMeta },
      })
    }
  }

  // DM톡(Nol Chat) : nol chat 데이터가 있는 경우 노출
  if (!!nolChat?.isAvailable) {
    buttons.push({
      type: OrderButtonType.SECONDARY,
      title: !!nolChat.validInvitation ? '숙소와 1:1 채팅하기' : '채팅 문의 내역 보기',
      id: 'nol-chat',
      icon: OrderButtonIconType.CHAT,
      data: {
        // 카테고리(숙소) 예약 번호
        reservationId: id,
        kinesisLogMeta: {
          buttonName: !!nolChat.validInvitation ? '숙소와 1:1 채팅하기' : '채팅 문의 내역 보기',
          placeNo: propertyId,
          hashOrderId,
        },
      },
    })
  }

  return buttons
}

/**
 * 체크아웃 시간이 지났더라도 대기예약 상태라면 후기 작성 불가능
 */
export const getPromotionText = ({
  reviewWritable,
  isPendingReservation,
}: {
  reviewWritable?: ReviewWritableResponseV2
  isPendingReservation?: boolean
}) => {
  if (!reviewWritable) return ''

  const writable = reviewWritable.available
  const rewardType = reviewWritable.expectedReward?.type
  const rewardPoint = reviewWritable.expectedReward?.amount ?? 0

  return writable && rewardPoint > 0 && !isPendingReservation
    ? `후기 작성 시 ${rewardType === 'COIN' ? toCoin(rewardPoint) : toPoint(rewardPoint)} 적립`
    : ''
}

type CartDomesticOrder = Extract<
  OrderListV3Query['orderListV3']['orders'][number]['categoryOrders'][number],
  { __typename?: 'CartDomesticOrder' }
>
type OrderDetailsDomestic = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsDomestic' }
>

const isOrderDetailsDomesticOrder = isTypeOf<OrderDetailsDomestic>('OrderDetailsDomestic')
const isOrderListsDomesticOrder = isTypeOf<CartDomesticOrder>('CartDomesticOrder')

type Props = (CartDomesticOrder | OrderDetailsDomestic) & {
  rewardRate?: number
}

// 예약 내역, 상세 Converter
export const toCartDomesticOrder = (
  props: Props,
  currentAt: string,
  order: { id: string; hashOrderId: string }
): ConvertedOrder => {
  const {
    id,
    statusGroupCode,
    propertyName,
    roomTypeName,
    ratePlanTitle,
    checkInAt: checkIn,
    checkOutAt: checkOut,
    useType,
    visitType,
    pickupType,
    propertyId,
    property,
    roomType,
    isPenaltyAgree = false,
    cancelPolicySummary,
    pendingStatus,
    propertyCategory,
    benefits,
    memberClass,
  } = props

  const photoUrl = property?.data.photos[0]?.url || ''
  const pdpUrl = `${conf.place.url}/domestic/${propertyId}`
  const deeplink = `yanoljamotel://wv?url=${pdpUrl}`
  const isInfiniteCouponRoom = roomType?.data.cloneType === 'MY_ROOM'
  const roomTypeBadges = roomType?.config?.badges ?? []
  const status = getDomesticStatus(statusGroupCode, checkOut, currentAt)
  const isPendingReservation = getIsDomesticPendingReservation(pendingStatus, status)

  return {
    id,
    categoryOrderId: id,
    status,
    serviceType: SERVICE_TYPE.ACCOMMODATION,
    categoryType: CategoryType.DomesticReservation,
    imageSrc: photoUrl,
    title: propertyName,
    subTitle: roomTypeName,
    ratePlanTitle,
    info: getDomesticInfo({ checkIn, checkOut, useType, roomType }),
    options: getDomesticOptions({ benefits, visitType, pickupType }),
    buttons: getDomesticButtons({ props, currentAt, isPendingReservation, order }),
    promotion: isOrderDetailsDomesticOrder(props)
      ? getPromotionText({ reviewWritable: props.reviewWritableV2, isPendingReservation })
      : '',
    price: isOrderDetailsDomesticOrder(props) ? props.price : undefined,
    pdpUrl,
    isInfiniteCouponRoom,
    deeplink,
    useEndDate: isoToFormat(checkOut, 'yyyy-MM-dd'),
    useTypeText: getHasDayUseBadge(roomTypeBadges) ? 'DayUse' : UseTypeText[useType] || '',
    cancelPolicySummary: getDomesticCancelPolicySummary({
      cancelPolicySummary,
      currentAt,
      checkIn,
      checkOut,
      statusGroupCode,
    }),
    hasPartialCancel: isOrderDetailsDomesticOrder(props) && props.hasPartialCancel,
    badges: getDomesticPendingReservationBadges(isPendingReservation).concat(
      getDomesticCancelPenaltyBadges(isPenaltyAgree)
    ),
    isPendingReservation,
    propertyCategory,
    vipMemberClassBadgeType: getDomesticVipBadgeType(memberClass),
    dailyPrices: isOrderDetailsDomesticOrder(props) ? (props.dailyPrices ?? undefined) : undefined,
  }
}

type SharedOrderDomestic = Extract<
  SharedOrderQuery['sharedOrder']['categoryOrders'][number],
  { __typename?: 'SharedOrderDomestic' }
>
export const toSharedDomesticOrder = function (order: SharedOrderDomestic) {
  const {
    propertyName,
    roomTypeName,
    ratePlanTitle,
    ratePlanDescription,
    checkInAt: checkIn,
    checkOutAt: checkOut,
    useType,
    visitType,
    pickupType,
    id,
    pendingStatus,
    roomType,
    benefits,
    propertyCategory,
  } = order
  const photoUrl = order.property?.data.photos[0]?.url || ''

  // 공유하기 내 상품들은 유효한 상품들(STATUS.READY)만 validate해 노출된다.
  const isPendingReservation = getIsDomesticPendingReservation(pendingStatus, STATUS.READY)
  return {
    categoryOrderId: id,
    serviceType: SERVICE_TYPE.ACCOMMODATION,
    title: propertyName,
    subTitle: roomTypeName,
    ratePlanTitle,
    ratePlanDescription,
    info: getDomesticInfo({ checkIn, checkOut, useType, roomType }),
    options: getDomesticOptions({ benefits, visitType, pickupType }),
    imageSrc: photoUrl,
    id,
    isPendingReservation,
    badges: getDomesticPendingReservationBadges(isPendingReservation),
    propertyCategory,
  }
}

export type OrderDetailsCancellationDomesticOrder = Extract<
  OrderDetailsCancellationCategoryOrders[number],
  { __typename?: 'OrderDetailsCancellationDomestic' }
>
export const toCancellationDomesticOrder = (
  order: OrderDetailsCancellationDomesticOrder,
  categoryOrderId: string
) => {
  const {
    id,
    statusGroupCode: status,
    propertyName,
    roomTypeName,
    ratePlanTitle,
    checkInAt: checkIn,
    checkOutAt: checkOut,
    useType,
    visitType,
    pickupType,
    price,
    commissionAmount,
    rewardStatus,
    rewardAmount,
    isPenaltyAgree = false,
    roomType,
    benefits,
    memberClass,
  } = order
  const photoUrl = order.property?.data.photos[0]?.url || ''
  return {
    id,
    categoryOrderId,
    serviceType: SERVICE_TYPE.ACCOMMODATION,
    status: getCancellationDomesticStatus(status),
    imageSrc: photoUrl,
    title: propertyName,
    subTitle: roomTypeName,
    ratePlanTitle,
    info: getDomesticInfo({ checkIn, checkOut, useType, roomType }),
    options: getDomesticOptions({ benefits, visitType, pickupType }),
    refundPrice: price,
    commissionAmount,
    rewardStatus,
    rewardAmount,
    badges: getDomesticCancelPenaltyBadges(isPenaltyAgree),
    vipMemberClassBadgeType: getDomesticVipBadgeType(memberClass),
  }
}

type OrderCompleteCategoryOrder = OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number]
export type OrderCompleteProductDomestic = Extract<
  OrderCompleteCategoryOrder,
  { __typename?: 'OrderDetailsDomestic' }
>
export type TViewer = { role: Role }

// 예약 완료 Converter
export const toOrderCompleteProductDomestic = ({
  order,
  categoryOrderId,
  currentAt,
}: {
  order: OrderCompleteProductDomestic
  categoryOrderId: string
  currentAt: string
}) => {
  const {
    id,
    propertyName,
    roomTypeName,
    ratePlanTitle,
    checkInAt: checkIn,
    checkOutAt: checkOut,
    useType,
    visitType,
    roomType,
    pickupType,
    property,
    cancelPolicySummary,
    pendingStatus,
    statusGroupCode,
    ratePlan,
    propertyCategory,
    benefits,
    memberClass,
    ratePlanConfig,
  } = order
  const photoUrl = order.property?.data.photos[0]?.url || ''
  const isInfiniteCouponRoom = roomType?.data.cloneType === 'MY_ROOM'
  const roomTypeBadges = roomType?.config?.badges ?? []
  const isPendingReservation = getIsDomesticPendingReservation(pendingStatus, STATUS.COMPLETED)
  const badges = badgeWithPolicyInOrder({
    propertyBadges: property?.config?.badges ?? [],
    optionBadges: roomTypeBadges,
    ratePlanBadges: ratePlanConfig?.badges ?? [],
    useType: useType,
  }).concat(getDomesticPendingReservationBadges(isPendingReservation))

  return {
    id,
    categoryOrderId,
    serviceType: SERVICE_TYPE.ACCOMMODATION,
    status: STATUS.READY, // 주문 완료 화면에서는 상태를 표현하지 않음
    imageSrc: photoUrl,
    title: propertyName,
    subTitle: roomTypeName,
    ratePlanTitle,
    info: getDomesticInfo({ checkIn, checkOut, useType, roomType }),
    options: getDomesticOptions({ benefits, visitType, pickupType }),
    isInfiniteCouponRoom,
    categoryType: CategoryType.DomesticReservation,
    useEndDate: isoToFormat(checkOut, 'yyyy-MM-dd'),
    badges,
    cancelPolicySummary: getDomesticCancelPolicySummary({
      cancelPolicySummary,
      checkIn,
      checkOut,
      currentAt,
      statusGroupCode,
    }),
    isPendingReservation,
    isExceptionalCancelPolicy: ratePlan.isExceptionalCancelPolicy,
    propertyCategory,
    vipMemberClassBadgeType: getDomesticVipBadgeType(memberClass),
  }
}

export const toOrderCompleteBrazeCustomDomestic = ({
  order,
  orderDetails,
  isNolPayment,
}: {
  order: OrderCompleteProductDomestic
  orderDetails: OrderCompleteV3Query['orderDetailsV3']
  isNolPayment: boolean
}): IConverterBrazeCompositeOrderComplete => {
  const {
    propertyCategory,
    propertyId,
    propertyName,
    checkInAt,
    checkOutAt,
    useType,
    roomTypeName,
    roomTypeId,
    adults,
    childrenAges,
    visitType,
    property,
  } = order
  const { createdAt, id } = orderDetails
  const formatDate = date => DateTime.fromISO(date).toFormat('yyyy-MM-dd')
  const isSameDate = formatDate(createdAt) === formatDate(checkInAt)
  const isAfterDate =
    (DateTime.fromISO(checkInAt).diff(DateTime.fromISO(createdAt), 'days').toObject().days || 0) > 0
  const dayuse_included = isDayUse(useType, propertyCategory)
  const [address1 = '', address2 = ''] = (property?.data?.address ?? '').split(' ')
  const city_name = [address1, address2].join(' ')
  return {
    category: getBrazeOrderCategory(order),
    composite_order_id: id,
    property_category: propertyCategory,
    item_id: propertyId.toString(),
    item_name: propertyName,
    check_in_date: DateTime.fromISO(checkInAt).toISO(),
    check_out_date: DateTime.fromISO(checkOutAt).toISO(),
    use_type: useType.toString(),
    is_earlybird: !isSameDate && isAfterDate,
    dayuse_included,
    item_detail_id: roomTypeId.toString(),
    item_detail_name: roomTypeName,
    cust_count: adults,
    visitType: BrazeVisitType[visitType],
    has_children: !!childrenAges?.length,
    ...getOrderCompleteBrazeCouponAndPointInfo({
      orderDetails,
      coupon: order.domesticDiscount?.coupons[0],
      price: order.price,
    }),
    nolcard_approval: isNolPayment,
    city_name,
  }
}

export const getOrderCompleteFacebookPixelDomesticData = ({
  order,
}: {
  order: OrderCompleteProductDomestic
}): IFbqPurchaseData => {
  const [address1 = '', address2 = ''] = (order.property?.data?.address ?? '').split(' ')
  const address = [address1, address2].join(' ')
  return {
    content_type: 'hotel',
    checkin_date: DateTime.fromISO(order.checkInAt).toISO(),
    checkout_date: DateTime.fromISO(order.checkOutAt).toISO(),
    content_ids: `P-${order.propertyId}`,
    city: address,
    region: address,
    num_adults: order.adults,
    num_children: order?.childrenAges?.length ?? 0,
    value: order.price,
    currency: 'KRW',
  }
}

export const toReceiptDomestic = (
  order: OrderDetailsDomestic,
  categoryOrderId: string
): ConvertedOrder => {
  const {
    id,
    statusGroupCode,
    propertyName,
    roomTypeName,
    ratePlanTitle,
    checkInAt: checkIn,
    checkOutAt: checkOut,
    useType,
    visitType,
    pickupType,
    property,
    hasPartialCancel,
    pendingStatus,
    roomType,
    benefits,
    memberClass,
    propertyCategory,
  } = order
  const status = getDomesticStatus(statusGroupCode, checkOut)
  return {
    id,
    status,
    title: propertyName,
    subTitle: roomTypeName,
    ratePlanTitle,
    serviceType: SERVICE_TYPE.ACCOMMODATION,
    categoryOrderId,
    imageSrc: property?.data.photos[0]?.url || '',
    info: getDomesticInfo({ checkIn, checkOut, useType, roomType }),
    options: getDomesticOptions({ benefits, visitType, pickupType }),
    hasPartialCancel,
    categoryType: CategoryType.DomesticReservation,
    price: isOrderDetailsDomesticOrder(order) ? order.price : undefined,
    useTypeText: UseTypeText[useType] || '',
    isPendingReservation: getIsDomesticPendingReservation(pendingStatus, status),
    vipMemberClassBadgeType: getDomesticVipBadgeType(memberClass),
    propertyCategory,
  }
}

export const toOrderCompleteBrazePurchaseDomestic = (
  order: OrderCompleteProductDomestic,
  payments: OrderCompleteV3Query['orderDetailsV3']['payments'],
  paid_amount: number
): IBrazePurchaseEventLog => {
  const { propertyCategory, propertyId, price } = order

  const payment_method = payments.map(payment => payment?.paymentMethod?.code || '')

  return {
    productId: propertyId.toString(),
    price,
    currencyCode: Currency.KRW,
    purchaseProperties: {
      category: Category[propertyCategory.toUpperCase()],
      payment_method,
      paid_amount,
    },
  }
}

/**
 * @param order: order detail query item
 * @return isAvailable: 셀프체크인 진입 불가 여부 -> 대기 예약 상태는 진입 불가
 */
export const getIsAvailableOrderDetailDomesticSelfCheckIn = (
  order: OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number] | null
): boolean => {
  if (!order || !isOrderDetailsDomesticOrder(order)) {
    return false
  }
  // 취소 상태 + 대기 예약 상태인 경우 셀프체크인 진입 불가하도록 막기 위해 READY 로 고정.
  return !getIsDomesticPendingReservation(order.pendingStatus, STATUS.READY)
}
